import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Zap,
  Droplets,
  LucideIcon,
  LeafyGreen,
  Leaf,
  Wind,
  ClipboardList,
  Compass,
  FileBarChart2,
  Sprout,
  Bell,
  LayoutDashboard,
  TrendingUp,
  PenLine,
  ClipboardCheck,
  FileEdit,
  Building2,
  Users,
  Activity,
  Gauge,
  BookOpen,
  HardHat,
  BrainCircuit,
  LogOut,
  Target,
} from "lucide-react";
import "./Sidebar.css";
import logoNew from "../../Assets/logo_new.jpeg";

import type { User as BackendUser } from "../../types/user";

interface SidebarProps {
  user?: BackendUser | null;
  isOpen?: boolean;
  onToggle?: () => void;
  onLogout?: () => void;
}

type MenuItem = {
  id: string;
  label: string;
  icon: string | LucideIcon;
  color: string;
  path?: string;
  children?: MenuItem[];
  adminOnly?: boolean;
  allowedRoles?: string[];
  alwaysColored?: boolean;
  wip?: boolean;
  sectionLabel?: string;
};

const Sidebar = ({ user, isOpen = true, onToggle, onLogout }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const menuItems: MenuItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      color: "#3b82f6",
      path: "/dashboard",
    },
    {
      id: "alignement-referentiel",
      label: "Alignement référentiel",
      icon: Target,
      color: "#8b5cf6",
      path: "/alignement-referentiel",
    },
    {
      id: "pilotage-rse",
      label: "Pilotage RSE",
      icon: Gauge,
      color: "#7c3aed",
      children: [
        {
          id: "diagnostic-rse",
          label: "Diagnostic RSE",
          icon: ClipboardList,
          color: "#e11d48",
          path: "/diagnostic-rse",
          alwaysColored: true,
          wip: true,
        },
        {
          id: "strategie-rse",
          label: "Strategie RSE",
          icon: Compass,
          color: "#0037dc",
          path: "/strategie-rse",
          alwaysColored: true,
          wip: true,
        },
        {
          id: "veille-rse",
          label: "Veille RSE",
          icon: Bell,
          color: "#0891b2",
          path: "/veille-rse",
          alwaysColored: true,
        },
      ],
    },
    {
      id: "bilan-carbone",
      label: "Bilan Carbone",
      icon: LeafyGreen,
      color: "#059669",
      children: [
        {
          id: "empreinte-carbone",
          label: "Empreinte Carbone",
          icon: Leaf,
          color: "#10b981",
          path: "/carbon",
          alwaysColored: true,
        },
        {
          id: "decarbonation",
          label: "Décarbonation",
          icon: Wind,
          color: "#0891b2",
          path: "/carbon/decarbonation",
          alwaysColored: true,
          wip: true,
        },
      ],
    },
    {
      id: "data-analysis",
      label: "Analyse de données",
      icon: TrendingUp,
      color: "#008f94",
      children: [
        {
          id: "yameera-analyst",
          label: "Yameera Analyst",
          icon: BrainCircuit,
          color: "#6366f1",
          path: "/data-analysis/yameera-analyst",
          alwaysColored: true,
        },
        {
          id: "energie",
          label: "Energie",
          icon: Zap,
          color: "#f59e0b",
          path: "/data-analysis/energie",
          alwaysColored: true,
        },
        {
          id: "eau",
          label: "Eau",
          icon: Droplets,
          color: "#3b82f6",
          path: "/data-analysis/eau",
          alwaysColored: true,
        },
      ],
    },
    {
      id: "reporting",
      label: "Reporting",
      icon: BookOpen,
      color: "#0f766e",
      children: [
        {
          id: "rapport-indicateurs",
          label: "Rapport d'indicateurs",
          icon: FileBarChart2,
          color: "#d97706",
          path: "/rapport-indicateurs",
          alwaysColored: true,
        },
        {
          id: "rapport-durabilite",
          label: "Rapport durabilité",
          icon: Sprout,
          color: "#15803d",
          path: "/rapport-durabilite",
          alwaysColored: true,
          wip: true,
        },
      ],
    },
    {
      id: "data-entry",
      label: "Panel des saisies",
      sectionLabel: "Administration",
      icon: PenLine,
      color: "#16a34a",
      children: [
        {
          id: "canevas",
          label: "Canevas et validation",
          icon: ClipboardCheck,
          color: "#d92020ff",
          path: "/data-entry/canevas",
          allowedRoles: ["admin", "superuser", "user"],
        },
        {
          id: "saisie",
          label: "Saisie de Données",
          icon: FileEdit,
          color: "#1063d7ff",
          path: "/data-entry/validation",
          allowedRoles: ["admin", "agent"],
        },
      ],
    },
    {
      id: "organisation",
      label: "Organisation",
      icon: Building2,
      color: "#125a8eff",
      adminOnly: true,
      children: [
        {
          id: "gestion-interne",
          label: "Gestion interne",
          icon: Users,
          color: "#7c3aed",
          path: "/organisation/gestion-interne",
          adminOnly: true,
        },
        {
          id: "suivi-saisies",
          label: "Suivi des saisies",
          icon: Activity,
          color: "#059669",
          path: "/organisation/suivi-saisies",
          adminOnly: true,
        },
      ],
    },
  ];

  // normalize user role for checks
  const userRole = (user?.role ?? "").toString().toLowerCase().trim();
  const isAdmin = userRole === "admin" || userRole === "administrator";

  // Check if user has access to a menu item
  const hasAccess = (item: MenuItem): boolean => {
    // Check adminOnly flag
    if (item.adminOnly && !isAdmin) {
      return false;
    }

    // Check allowedRoles if specified
    if (item.allowedRoles && item.allowedRoles.length > 0) {
      return item.allowedRoles.includes(user?.role || "");
    }

    // Default: allow access if no restrictions
    return true;
  };

  // Filter menu items and their children based on user role
  const filteredMenuItems = menuItems
    .filter(hasAccess)
    .map((item) => ({
      ...item,
      children: item.children?.filter(hasAccess),
    }))
    .filter((item) => {
      // Keep items that have no children, or have at least one visible child
      return !item.children || item.children.length > 0;
    });

  // determine active path (safe for optional path)
  const isActive = (path?: string) => !!path && location.pathname === path;

  // Check if any child is active
  const isChildActive = (children?: MenuItem[]) => {
    if (!children) return false;
    return children.some((child) => isActive(child.path));
  };

  // Toggle expansion of menu items with children
  const toggleExpansion = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  // Handle click - expand if has children, navigate if has path
  const handleItemClick = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      toggleExpansion(item.id);
    } else if (item.path) {
      navigate(item.path);
      // Close sidebar on mobile after navigation
      if (onToggle && window.innerWidth < 768) {
        onToggle();
      }
    }
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? "open" : ""}`} onClick={onToggle} />
      <aside className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <button
            className="sidebar-menu-button"
            onClick={() => {
              console.log("[SIDEBAR] Menu button clicked!");
              if (onToggle) {
                console.log("[SIDEBAR] Calling onToggle function");
                onToggle();
              } else {
                console.log("[SIDEBAR] onToggle is not defined!");
              }
            }}
            aria-label="Toggle sidebar"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div className="sidebar-brand">
            <img src={logoNew} alt="Yameera" className="sidebar-logo-img" />
            <span className="sidebar-brand-name">Yameera</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {filteredMenuItems.map((item) => (
            <div key={item.id}>
              {item.sectionLabel && (
                <div className="sidebar-section-label">{item.sectionLabel}</div>
              )}
              <button
                onClick={() => handleItemClick(item)}
                className={`sidebar-item ${isActive(item.path) || isChildActive(item.children) ? "active" : ""
                  }`}
                style={
                  isActive(item.path) || isChildActive(item.children)
                    ? {
                      backgroundColor: `${item.color}15`,
                      color: item.color,
                    }
                    : {}
                }
              >
                {typeof item.icon === "string" ? (
                  <img
                    src={item.icon}
                    alt={item.label}
                    className="sidebar-icon"
                    style={{
                      width: "24px",
                      height: "24px",
                      objectFit: "contain",
                      marginRight: "8px",
                    }}
                  />
                ) : (
                  <item.icon
                    size={24}
                    className="sidebar-icon"
                    style={{
                      marginRight: "8px",
                      color: item.color,
                    }}
                  />
                )}
                <span className="sidebar-label">{item.label}</span>
                {item.wip && (
                  <HardHat size={13} className="sidebar-wip-icon" />
                )}
                {/* Add expand/collapse indicator for items with children */}
                {item.children && item.children.length > 0 && (
                  <span
                    className="expand-indicator"
                    style={{
                      marginLeft: "auto",
                      transform: expandedItems.includes(item.id) ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    ▶
                  </span>
                )}
              </button>

              {/* render children if expanded and has children */}
              {item.children &&
                expandedItems.includes(item.id) &&
                item.children.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => {
                      if (child.path) {
                        navigate(child.path);
                        // Close sidebar on mobile after navigation
                        if (onToggle && window.innerWidth < 768) {
                          onToggle();
                        }
                      }
                    }}
                    className={`sidebar-item sidebar-child ${isActive(child.path) ? "active" : ""}`}
                    style={
                      isActive(child.path)
                        ? {
                          backgroundColor: `${child.color}10`,
                          color: child.color,
                          marginLeft: 20,
                        }
                        : { marginLeft: 20 }
                    }
                  >
                    {typeof child.icon === "string" ? (
                      <img
                        src={child.icon}
                        alt={child.label}
                        className="sidebar-icon"
                        style={{
                          width: "20px",
                          height: "20px",
                          objectFit: "contain",
                          marginRight: "8px",
                        }}
                      />
                    ) : (
                      <child.icon
                        size={20}
                        className="sidebar-icon"
                        style={{
                          marginRight: "8px",
                          color: child.color,
                        }}
                      />
                    )}
                    <span className="sidebar-label">{child.label}</span>
                    {child.wip && (
                      <HardHat size={12} className="sidebar-wip-icon" />
                    )}
                  </button>
                ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <div className="sidebar-divider" />
          <button
            className="sidebar-item sidebar-logout-btn"
            onClick={() => {
              try { onLogout?.(); } catch (_) {}
              window.location.replace(`${process.env.PUBLIC_URL ?? ""}/login`);
            }}
            title={!isOpen ? "Déconnexion" : undefined}
          >
            <LogOut size={24} className="sidebar-icon" style={{ marginRight: "8px", color: "#ef4444" }} />
            <span className="sidebar-label" style={{ color: "#ef4444" }}>Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
